import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from './../../environments/environment';
import { Router, ActivatedRoute } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { getCurrentBrand } from 'app/config/brands';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<any>;
    public currentUser: Observable<any>;
    private currentOfficeSubject: BehaviorSubject<any>;
    public currentOffice: Observable<any>;
    private currentAgencySubject: BehaviorSubject<any>;
    public currentAgency: Observable<any>;
    currentLang: string;

    // Temporary storage for user data during 2FA verification
    private tempUserData: any = null;

    // .immosurance.net / .immokredita.com, depending on the domain of the page (#183)
    private readonly cookieDomain = getCurrentBrand().cookieDomain;

    constructor(
        private http: HttpClient,
        private router: Router,
        private _cookieService: CookieService,
        private _translateService: TranslateService
    ) {
        this._translateService.onLangChange
            .subscribe(() => {
                this.currentLang = this._translateService.currentLang;
            });

        this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();
        this.currentOfficeSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('currentOffice')));
        this.currentOffice = this.currentOfficeSubject.asObservable();
        this.currentAgencySubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('currentAgency')));
        this.currentAgency = this.currentAgencySubject.asObservable();
    }

    public get currentUserValue(): any {
        return this.currentUserSubject.value;
    }

    public get currentOfficeValue(): any {
        return this.currentOfficeSubject.value;
    }

    public get currentAgencyValue(): any {
        return this.currentAgencySubject.value;
    }

    // Get temporary user data during 2FA verification
    public get tempUserDataValue(): any {
        return this.tempUserData;
    }

    // Get current user data (from localStorage or temporary storage)
    public get currentUserData(): any {
        return this.currentUserValue || this.tempUserDataValue;
    }

    public setCurrentUser(data: any): any {
        localStorage.setItem('currentUser', JSON.stringify(data));
        this.currentUserSubject.next(data);
    }

    public setCurrentOffice(data: any): any {
        localStorage.setItem('currentOffice', JSON.stringify(data));
        this.currentOfficeSubject.next(data);
    }

    public setCurrentAgency(data: any): any {
        localStorage.setItem('currentAgency', JSON.stringify(data));
        this.currentAgencySubject.next(data);
    }

    // Store temporary user data for 2FA verification
    public setTempUserData(user: any): void {
        this.tempUserData = user;
        localStorage.setItem('tempUserData', JSON.stringify(user));
        // Set cookies for API access during 2FA
        if (user && user.access_token) {
            this._cookieService.set('accessToken', user.access_token, 1, '/', this.cookieDomain);
            this._cookieService.set('userId', user.user_id, 1, '/', this.cookieDomain);
        }
    }

    // Get temporary user data for 2FA verification
    public getTempUserData(): any {
        if (!this.tempUserData) {
            const storedData = localStorage.getItem('tempUserData');
            if (storedData) {
                this.tempUserData = JSON.parse(storedData);
            }
        }
        return this.tempUserData;
    }

    // Clear temporary user data
    public clearTempUserData(): void {
        this.tempUserData = null;
        localStorage.removeItem('tempUserData');
        this._cookieService.delete('accessToken', '/', this.cookieDomain);
        this._cookieService.delete('userId', '/', this.cookieDomain);
    }

    // Check if user should bypass 2FA
    public shouldBypassTwoFactor(user: any): boolean {
        return user?.user_role === 'admin';
    }

    // Helper method to add two-step verification status to user data
    private addTwoStepVerification(user: any): any {
        return {
            ...user,
            two_step_verified: true
        };
    }

    login(username: string, password: string): Observable<any> {
        // brand: only used by the backend when the domain does not tell it (localhost, #183)
        const brand = getCurrentBrand().key;
        return this.http.post<any>(`${environment.yiiUrl}users/login`, { username, password, brand })
            .pipe(map(user => {
                if (this.shouldBypassTwoFactor(user)) {
                    return this.saveLoggedinUserData(this.addTwoStepVerification(user));
                } else {
                    this.setTempUserData(user);
                    return user;
                }
            }));
    }

    /** adminToken: the Super User's own token for "Login to user", which may open an account of the other brand (#183). */
    loginByToken(accessToken: string, adminToken?: string): Observable<any> {
        const options = adminToken ? { headers: new HttpHeaders({ Authorization: `Bearer ${adminToken}` }) } : {};
        return this.http.get<any>(`${environment.yiiUrl}users/login-by-token?accessToken=${accessToken}&brand=${getCurrentBrand().key}`, options)
            .pipe(map(user => {
                return this.saveLoggedinUserData(this.addTwoStepVerification(user));
            }));
    }

    /**
     * "Login to user" of a user of the other brand (#183): one-time code for that user's own
     * domain, created with the Super User's session. Returns { url } of the other domain's login page.
     */
    createLoginHandover(userId: string): Observable<{ url: string }> {
        return this.http.post<{ url: string }>(`${environment.yiiUrl}users/create-login-handover`, { user: userId });
    }

    /** Logs in with a hand-over code on the user's own domain (login page ?handover=…). */
    redeemLoginHandover(code: string): Observable<any> {
        return this.http.post<any>(`${environment.yiiUrl}users/redeem-login-handover`, { code })
            .pipe(map(user => {
                return user ? this.saveLoggedinUserData(this.addTwoStepVerification(user)) : null;
            }));
    }

    loginByUser(userData: any): Observable<any> {
        return this.http.post<any>(`${environment.yiiUrl}users/admin-login-as`, userData)
            .pipe(map(user => {
                return this.saveLoggedinUserData(this.addTwoStepVerification(user));
            }));
    }

    saveLoggedinUserData(user: any): any {
        if (user && user.access_token) {
            this._cookieService.set('accessToken', user.access_token, 1, '/', this.cookieDomain);
            this._cookieService.set('userId', user.user_id, 1, '/', this.cookieDomain);
            this.setCurrentUser(user);
            if (user.user_agency_data) {
                this.setCurrentAgency(user.user_agency_data);
            }
            if (user.user_office_data) {
                this.setCurrentOffice(user.user_office_data);
            }
        }
        return user;
    }

    // Save user data after successful 2FA verification
    public saveUserDataAfterTwoFactor(): void {
        if (this.tempUserData) {
            const userData = {
                ...this.addTwoStepVerification(this.tempUserData),
                two_factor_auth_verified: true
            };
            this.saveLoggedinUserData(userData);
            this.clearTempUserData();
        }
    }

    emailValidation(username: string): Observable<any> {
        return this.http.get<any>(`${environment.frontendUrl}index.php?r=site/request-password-reset&email=${username}&environment=V2&lang=${this.currentLang}`);
    }

    resetPassword(password: string, token: any): Observable<any> {
        return this.http.post<any>(
            `${environment.frontendUrl}index.php?r=site/reset-password`,
            { token, pass: password }
        );
    }
    // Sending password directly into route can create issue as it contains special characters - converted to POST
    setInvitePassword(password: string, token: string): Observable<any> {
        return this.http.post<any>(
            `${environment.frontendUrl}index.php?r=site/set-invite-password`,
            { password, token }
        );
    }
    logout(redirect = true): void {
        this._cookieService.delete('accessToken', '/', this.cookieDomain);
        this._cookieService.delete('userId', '/', this.cookieDomain);
        localStorage.removeItem('lang');
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
        localStorage.removeItem('currentOffice');
        this.currentOfficeSubject.next(null);
        localStorage.removeItem('currentAgency');
        this.currentAgencySubject.next(null);
        this.clearTempUserData();
        if (redirect) {
            this.router.navigate(['/login']);
        }
    }
    /**
     * Get the current user's real-time 2FA status from the server.
     * This is the authoritative source — never rely on localStorage for this.
     */
    get2FAStatus(): Observable<{ two_factor_auth_verified: boolean }> {
        return this.http.get<any>(`${environment.apiUrl}users/2fa/status`);
    }

    twoFactorRegistration(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}users/2fa/authenticator/setup`, {});
    }

    twoFactorVerification(token: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}users/2fa/authenticator/verify`, { token })
            .pipe(map(res => {
                if (res.verified) {
                    // Save user data after successful 2FA verification
                    this.saveUserDataAfterTwoFactor();
                }
                return res;
            }));
    }

    sendEmailOtp(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}users/2fa/email/send-otp`, {});
    }

    verifyEmailOtp(code: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}users/2fa/email/verify`, { code })
            .pipe(map(res => {
                if (res.verified) {
                    // Save user data after successful 2FA verification
                    this.saveUserDataAfterTwoFactor();
                }
                return res;
            }));
    }

    verifyRecoveryCode(recoveryCode: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}users/2fa/recovery-codes/verify`, { recovery_code: recoveryCode })
            .pipe(map(res => {
                if (res.verified) {
                    this.saveUserDataAfterTwoFactor();
                }
                return res;
            }));
    }

    /**
     * Download 2FA recovery codes
     */
    download2FARecoveryCodes(user_id: string): Observable<any> {
        return this.http.post(`${environment.apiUrl}users/2fa/recovery-codes/download`,
            { user_id },
            {
                responseType: 'text',
                observe: 'response'
            }
        );
    }

    /**
     * Regenerate 2FA recovery codes
     */
    regenerate2FARecoveryCodes(user_id: string): Observable<any> {
        return this.http.post(`${environment.apiUrl}users/2fa/recovery-codes/regenerate`, { user_id });
    }

    /**
     * Reset user's 2FA settings (Admin only)
     * @param user_id The ID of the user whose 2FA settings should be reset
     */
    resetUser2FA(user_id: string): Observable<any> {
        return this.http.post(`${environment.apiUrl}users/2fa/reset`, { user_id });
    }
}
